import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useInvestorGetCertificatesQuery } from '@/modules/investor-dashboard/queries/certificates.ts';
import { useIssuerGetCertificatesQuery } from '@/modules/issuer-dashboard/queries/certificates.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { GetListByIssuerV1SrCertificatesIssuerGetParams, ShareCertificateResponse } from 'api';

interface CertificatesSelectPaginateProps {
  label?: string;
  error?: string;
  value: SelectPaginateOption<ShareCertificateResponse>[];
  disabled?: boolean;
  className?: string;
  classNameSelect?: string;
  onChange: (option: SelectPaginateOption<ShareCertificateResponse>[]) => void;
  role?: 'investor' | 'issuer';
  filters?: Pick<
    GetListByIssuerV1SrCertificatesIssuerGetParams,
    'from_date' | 'to_date' | 'status'
  >;
}

export const CertificatesSelectPaginate = ({
  error,
  value,
  disabled,
  onChange,
  className,
  classNameSelect,
  label = 'Certificates',
  role = 'issuer',
  filters,
}: CertificatesSelectPaginateProps) => {
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  const getFunction =
    role === 'issuer' ? useIssuerGetCertificatesQuery : useInvestorGetCertificatesQuery;

  const { fetchNextPage } = getFunction({
    page: 1,
    page_size: 15,
    status: filters?.status,
    from_date: filters?.from_date,
    to_date: filters?.to_date,
  });

  const handleAsync = useCallback(
    async (page: number) => {
      const certificatesData = await fetchNextPage({ pageParam: page });
      const certificates = certificatesData?.data?.pages?.at(-1)?.data || [];
      return certificates.map(item => {
        const { entity, register_data } = item;
        return {
          label: (
            <div className="text-sm leading-[22px] font-[Inter] flex items-center gap-2">
              <div className="text-gray-900 whitespace-nowrap 2xl:max-w-[350px] sm:max-w-[200px] max-w-[130px] overflow-hidden text-ellipsis">
                {role === 'issuer' && <>{entity?.entity_name ?? '---'}</>}
                {role === 'investor' && <>{register_data?.name ?? '---'}</>}
              </div>
            </div>
          ),
          value: { ...item, toString: () => String(item.id) },
        };
      });
    },
    [fetchNextPage, role],
  );

  return (
    <SelectPaginate
      isMulti
      className={className}
      classNameSelect={classNameSelect}
      value={value}
      error={error}
      disabled={disabled}
      onAsync={handleAsync}
      label={label}
      onChange={onChange}
      cacheUniqs={[registerId]}
    />
  );
};
